/* Showroom layout on phones (Steve-B 30-10-2026, design B "Showroom-app").
   Loaded by teak-product-showcase when "Phone: showroom layout" is on.

   <teak-cfg>  The inline size and colour picker. A dining table also gets a
               top view that grows with the size and seats the chairs from
               custom.seats_max. A choice commits through teak:variant-choose,
               the path the dialog picker uses, so the native radios stay the
               source of truth for price, cart and waitlist. Tablet and
               desktop keep the dialog picker.
   Also: the photo dots, story photos into the lightbox, the price on the
   buy button and the size line in the sticky bar. */
(() => {
  if (window.__teakShowroom) return;
  window.__teakShowroom = true;

  const esc = value => String(value ?? '').replace(/[&<>"']/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]));
  const NUM = '(\\d+(?:\\.\\d+)?)';

  function money(cents, currency) {
    let out;
    try {
      out = new Intl.NumberFormat(document.documentElement.lang || 'en-AU', {
        style: 'currency', currency: currency || 'AUD', currencyDisplay: 'narrowSymbol'
      }).format(cents / 100);
    } catch (e) {
      out = '$' + (cents / 100).toFixed(2);
    }
    return out.replace(/\.00\b/g, '');
  }

  function uniqSorted(list) {
    return [...new Set(list)].sort((a, b) => parseFloat(a) - parseFloat(b));
  }

  // Measurement rows as the data script normalised them:
  // "L150 × W80 × H75 cm (Legs 7 × 7 cm)", "Ø120 × H77 cm", "L120 × D63 × H97 cm".
  function parseRow(str) {
    const text = String(str || '').split('(')[0].trim();
    if (!text) return null;
    const round = text.match(new RegExp('Ø\\s*' + NUM));
    if (round) return { kind: 'round', d: +round[1], text };
    const l = text.match(new RegExp('L\\s*' + NUM));
    const w = text.match(new RegExp('[WD]\\s*' + NUM));
    if (l && w) return { kind: 'rect', l: +l[1], w: +w[1], text };
    return { kind: 'text', text };
  }

  // Option values as they are sold: "L150 x W80 (Seats up to 6)",
  // "150 x 120cm", "200/150cm x 100cm", "D120 x H75", "120cm".
  function parseValue(value) {
    const bare = String(value || '').split('(')[0].trim();
    let m = bare.match(new RegExp('^L?\\s*' + NUM + '(?:\\s*/\\s*' + NUM + ')?\\s*(?:cm)?\\s*[x×]\\s*[WD]?\\s*' + NUM, 'i'));
    if (m) return { kind: 'rect', l: +m[1], lText: m[2] ? m[1] + '/' + m[2] : m[1], w: +m[3] };
    m = bare.match(new RegExp('^D\\s*' + NUM, 'i'));
    if (m) return { kind: 'round', d: +m[1] };
    m = bare.match(new RegExp('^' + NUM + '\\s*cm$', 'i'));
    if (m) return { kind: 'single', n: +m[1] };
    return null;
  }

  function cleanName(name) {
    const n = String(name).toLowerCase();
    if (/colou?r/.test(n)) return 'Colour';
    if (/size/.test(n)) return 'Size';
    return String(name);
  }

  function cleanValue(value) {
    return String(value).split('(')[0].trim()
      .replace(/\.00\b/g, '')
      .replace(/\s*AUD$/i, '')
      .replace(/(\d)\s*[x×]\s*([A-Z]?\d)/gi, '$1 × $2')
      .replace(/(\d)cm\b/gi, '$1 cm');
  }

  // "Up to 6" -> "Seats up to 6", "6–8" -> "Seats 6 to 8".
  function seatsLabel(variant) {
    let s = String(variant.seatsText || '').trim();
    if (!s && variant.chairs) s = 'Up to ' + variant.chairs;
    if (!s) return '';
    s = s.replace(/\s*[–-]\s*/g, ' to ');
    return 'Seats ' + (/^up to/i.test(s) ? 'up to' + s.slice(5) : s);
  }

  // Chairs around the table, in drawing pixels. Long sides first, one at
  // each end from six seats, two per end on the widest tables.
  function seatPoints(kind, x, y, w, h, n, square) {
    const pts = [];
    const gap = 14;
    if (kind === 'round') {
      const cx = x + w / 2, cy = y + h / 2, r = w / 2 + gap;
      for (let i = 0; i < n; i++) {
        const a = -Math.PI / 2 + (i * 2 * Math.PI) / n;
        pts.push([cx + r * Math.cos(a), cy + r * Math.sin(a)]);
      }
      return pts;
    }
    let top, bottom, left, right;
    if (square) {
      const per = [0, 0, 0, 0];
      for (let i = 0; i < n; i++) per[i % 4]++;
      [top, bottom, left, right] = per;
    } else {
      const ends = n >= 6 ? (h >= 0.66 * w && n >= 10 ? 2 : 1) : 0;
      left = right = ends;
      top = Math.ceil((n - 2 * ends) / 2);
      bottom = n - 2 * ends - top;
    }
    for (let i = 0; i < top; i++) pts.push([x + ((i + 0.5) * w) / top, y - gap]);
    for (let i = 0; i < bottom; i++) pts.push([x + ((i + 0.5) * w) / bottom, y + h + gap]);
    for (let i = 0; i < left; i++) pts.push([x - gap, y + ((i + 0.5) * h) / left]);
    for (let i = 0; i < right; i++) pts.push([x + w + gap, y + ((i + 0.5) * h) / right]);
    return pts;
  }

  class TeakCfg extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      const source = this.querySelector('[data-cfg]');
      if (!source) return;
      try { this.cfg = JSON.parse(source.textContent); } catch (e) { return; }
      this.root = this.closest('[data-teak-pshow]');
      this.variants = this.cfg.variants || [];
      if (!this.root || !this.variants.length) return;
      this.ready = true;

      this.variants.forEach(v => {
        v.row = parseRow(v.dims);
        const fromText = (String(v.seatsText || '').match(/\d+/g) || []).map(Number);
        v.chairs = parseInt(v.seats, 10) || (fromText.length ? Math.max(...fromText) : 0);
      });
      this.isTable = /table/i.test(this.cfg.type || '');
      this.current = this.variants.find(v => String(v.id) === String(this.cfg.selected)) || this.variants[0];
      this.build();

      // The drawing needs real geometry for every size, else it is left out.
      const geo = this.variants.map(v => v.row).filter(g => g && g.kind !== 'text');
      this.drawable = this.isTable && geo.length === this.variants.length && this.variants.some(v => v.chairs);
      if (this.drawable) {
        this.maxL = Math.max(...geo.map(g => (g.kind === 'round' ? g.d : g.l)));
        this.maxW = Math.max(...geo.map(g => (g.kind === 'round' ? g.d : g.w)));
      }

      const hasCard = this.axes.length > 0;
      this.innerHTML = hasCard
        ? '<div class="teak-cfg__card"><div class="teak-cfg__axes"></div><div class="teak-cfg__statics"></div>' +
          '<div class="teak-cfg__draw" aria-hidden="true" hidden><span class="teak-cfg__table"></span></div>' +
          '<div class="teak-cfg__sum" aria-live="polite"></div></div>'
        : '<div class="teak-cfg__statics teak-cfg__statics--solo"></div><div class="teak-cfg__sum" hidden></div>';
      this.axesEl = this.querySelector('.teak-cfg__axes');
      this.staticsEl = this.querySelector('.teak-cfg__statics');
      this.drawEl = this.querySelector('.teak-cfg__draw');
      this.tableEl = this.querySelector('.teak-cfg__table');
      this.sumEl = this.querySelector('.teak-cfg__sum');
      this.chairEls = [];

      this.addEventListener('click', e => {
        const opt = e.target.closest('.teak-cfg__opt');
        if (opt && this.contains(opt)) this.choose(Number(opt.dataset.axis), opt.dataset.key);
      });
      this.root.addEventListener('teak:variant-applied', e => {
        const v = this.variants.find(x => String(x.id) === String(e.detail.variant.id));
        if (v) { this.current = v; this.update(); }
      });

      this.update();
      this.root.classList.add('has-cfg');
      // Position the first drawing without animating it in from the corner.
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-live')));
      if (this.drawEl && 'ResizeObserver' in window) new ResizeObserver(() => this.draw()).observe(this.drawEl);
    }

    // One axis per real choice. A single "L x W" size becomes Length and
    // Width when one length comes in several widths (the Deluxe table).
    build() {
      const opts = this.cfg.options || [];
      const V = this.variants;
      const varies = opts.map((_, i) => new Set(V.map(v => v.options[i])).size > 1);
      this.statics = opts
        .map((name, i) => (!varies[i] && name !== 'Title' ? { name: cleanName(name), value: cleanValue(V[0].options[i]) } : null))
        .filter(Boolean);
      this.axes = [];

      if (opts.length === 1 && varies[0]) {
        const geo = V.map(v => parseValue(v.options[0]));
        const bench = /bench/i.test((this.cfg.type || '') + ' ' + (this.cfg.title || ''));
        if (geo.every(g => g && g.kind === 'rect')) {
          V.forEach((v, i) => { v.kL = geo[i].lText; v.kW = String(geo[i].w); });
          const widths = new Map();
          V.forEach(v => { if (!widths.has(v.kL)) widths.set(v.kL, new Set()); widths.get(v.kL).add(v.kW); });
          const square = V.every((v, i) => geo[i].l === geo[i].w);
          if ([...widths.values()].some(set => set.size > 1)) {
            this.axes.push({ label: 'Length', unit: 'cm', values: uniqSorted(V.map(v => v.kL)), of: v => v.kL });
            this.axes.push({ label: 'Width', unit: 'cm', values: uniqSorted(V.map(v => v.kW)), of: v => v.kW });
          } else {
            this.axes.push({
              label: square ? 'Size' : 'Length', unit: 'cm', values: uniqSorted(V.map(v => v.kL)), of: v => v.kL,
              show: square ? key => key + ' × ' + key : null
            });
          }
        } else if (V.every(v => v.row && v.row.kind === 'round') || (geo.every(g => g && g.kind === 'single') && /round/i.test(this.cfg.shape || ''))) {
          V.forEach((v, i) => { v.kD = String(v.row && v.row.kind === 'round' ? v.row.d : geo[i].n); });
          this.axes.push({ label: 'Diameter', unit: 'cm', values: uniqSorted(V.map(v => v.kD)), of: v => v.kD });
        } else if (geo.every(g => g && g.kind === 'single')) {
          V.forEach((v, i) => { v.kN = String(geo[i].n); });
          this.axes.push({ label: bench ? 'Length' : 'Size', unit: 'cm', values: uniqSorted(V.map(v => v.kN)), of: v => v.kN });
        }
      }

      if (!this.axes.length) {
        opts.forEach((name, i) => {
          if (!varies[i]) return;
          const raw = [...new Set(V.map(v => v.options[i]))];
          const cm = raw.every(x => /^\d+(?:\.\d+)?\s*cm$/i.test(String(x).trim()));
          this.axes.push({
            label: cleanName(name),
            colour: /colou?r/i.test(name),
            unit: cm ? 'cm' : '',
            values: cm ? uniqSorted(raw) : raw,
            of: v => v.options[i],
            show: cm ? key => String(parseFloat(key)) : key => cleanValue(key)
          });
        });
      }
    }

    // Keys an axis can show, given the choices on the axes before it.
    keysFor(k) {
      const axis = this.axes[k];
      const cur = this.current;
      return axis.values.filter(key => this.variants.some(v =>
        axis.of(v) === key && this.axes.every((b, i) => i >= k || b.of(v) === b.of(cur))));
    }

    choose(k, key) {
      const cur = this.current;
      const want = this.axes.map((a, i) => (i === k ? key : a.of(cur)));
      let pool = this.variants.filter(v => this.axes.every((a, i) => a.of(v) === want[i]));
      if (!pool.length) pool = this.variants.filter(v => this.axes.every((a, i) => i > k || a.of(v) === want[i]));
      // Sold-out sizes stay choosable (they open the waitlist), but an
      // in-stock match wins when the later axes are free.
      const pick = pool.find(v => v.available) || pool[0];
      if (pick && String(pick.id) !== String(cur.id)) {
        this.root.dispatchEvent(new CustomEvent('teak:variant-choose', { detail: { id: pick.id } }));
      }
    }

    update() {
      const cur = this.current;
      if (this.axesEl) {
        this.axesEl.innerHTML = this.axes.map((axis, k) => {
          const keys = this.keysFor(k);
          const label = key => (axis.show ? axis.show(key) : key);
          const seg = !axis.colour && keys.length <= 8 && keys.every(key => String(label(key)).length <= 4);
          const swatchOf = key => (axis.colour ? (this.variants.find(v => axis.of(v) === key && v.swatch) || {}).swatch : null);
          const hint = axis.colour ? esc(label(axis.of(cur))) : esc(axis.unit);
          const opts = keys.map(key => {
            const on = key === axis.of(cur);
            const inStock = this.variants.some(v => v.available && axis.of(v) === key &&
              this.axes.every((b, i) => i >= k || b.of(v) === b.of(cur)));
            const sw = swatchOf(key);
            const text = label(key);
            const aria = axis.label + ' ' + text + (axis.unit ? ' ' + axis.unit : '') + (inStock ? '' : ', sold out');
            return '<button type="button" role="radio" aria-checked="' + on + '" aria-label="' + esc(aria) + '"' +
              ' class="teak-cfg__opt' + (on ? ' is-on' : '') + (inStock ? '' : ' is-soldout') + (sw ? ' has-swatch' : '') + '"' +
              ' data-axis="' + k + '" data-key="' + esc(key) + '">' +
              (sw ? '<img src="' + esc(sw) + '" alt="" width="36" height="36" loading="lazy">' : '<span>' + esc(text) + '</span>') +
              '</button>';
          }).join('');
          const kind = seg ? 'seg' : axis.colour ? 'swatches' : 'chips';
          return '<div class="teak-cfg__axis"><div class="teak-cfg__head"><span class="teak-cfg__label">' + esc(axis.label) + '</span>' +
            '<span class="teak-cfg__hint">' + hint + '</span></div>' +
            '<div class="teak-cfg__' + kind + '" role="radiogroup" aria-label="' + esc(axis.label) + '"' +
            (seg ? ' style="--n:' + keys.length + '"' : '') + '>' + opts + '</div></div>';
        }).join('');
      }

      const seats = seatsLabel(cur);
      const dims = cur.row ? cur.row.text : (this.variants.length === 1 ? this.cfg.overall : '');
      // A size that cannot be chosen is already in the measurements.
      const rows = this.statics.filter(s => !(dims && s.name === 'Size')).map(s => [s.name, s.value]);
      if (!this.axes.length) {
        // Nothing to choose: a short spec list instead of a card.
        if (seats) rows.push(['Seats', seats.replace(/^Seats\s+/, '')]);
        if (dims) rows.push(['Dimensions', dims]);
      } else {
        // A sold-out choice stays selected (it opens the waitlist below), so say so here.
        const tag = cur.available ? '' : '<span class="teak-cfg__tag">Sold out</span>';
        this.sumEl.innerHTML = seats
          ? '<span class="teak-cfg__seats">' + esc(seats) + tag + '</span>' + (dims ? '<span class="teak-cfg__dims">' + esc(dims) + '</span>' : '')
          : dims ? '<span class="teak-cfg__dims teak-cfg__dims--solo">' + esc(dims) + tag + '</span>' : tag;
        this.sumEl.hidden = !seats && !dims && !tag;
      }
      this.staticsEl.innerHTML = rows.map(r =>
        '<p class="teak-cfg__static"><span>' + esc(r[0]) + '</span><span>' + esc(r[1]) + '</span></p>').join('');
      this.hidden = !this.axes.length && !rows.length;

      this.draw();

      // The short line in the sticky bar: "L200 × W100 · seats 6 to 8".
      let line = cur.row && cur.row.kind !== 'text'
        ? cur.row.text.replace(/\s*×\s*H\d+(?:\.\d+)?/i, '').replace(/\s*cm$/i, '')
        : this.axes.map(a => (a.show ? a.show(a.of(cur)) : a.of(cur)) + (a.unit ? ' ' + a.unit : '')).join(' · ');
      if (line && seats) line += ' · ' + seats.charAt(0).toLowerCase() + seats.slice(1);
      if (line) this.root.querySelectorAll('[data-teak-pshow-stickyvariant]').forEach(el => { el.textContent = line; });
    }

    draw() {
      if (!this.drawEl) return;
      const v = this.current;
      const g = v.row;
      if (!this.drawable || !g || g.kind === 'text' || !v.chairs) { this.drawEl.hidden = true; return; }
      this.drawEl.hidden = false;
      const W = this.drawEl.clientWidth || 300;
      const H = this.drawEl.clientHeight || 150;
      const M = 32;
      const s = Math.min((W - 2 * M) / this.maxL, (H - 2 * M) / this.maxW);
      const tw = (g.kind === 'round' ? g.d : g.l) * s;
      const th = (g.kind === 'round' ? g.d : g.w) * s;
      const x = (W - tw) / 2;
      const y = (H - th) / 2;
      Object.assign(this.tableEl.style, { left: x + 'px', top: y + 'px', width: tw + 'px', height: th + 'px' });
      this.tableEl.classList.toggle('is-round', g.kind === 'round');
      const pts = seatPoints(g.kind, x, y, tw, th, v.chairs, g.kind === 'rect' && g.l === g.w);
      while (this.chairEls.length < pts.length) {
        const chair = document.createElement('span');
        chair.className = 'teak-cfg__chair is-gone';
        const p = pts[this.chairEls.length];
        chair.style.left = p[0] - 8 + 'px';
        chair.style.top = p[1] - 8 + 'px';
        this.drawEl.appendChild(chair);
        this.chairEls.push(chair);
      }
      this.chairEls.forEach((chair, i) => {
        const p = pts[i];
        chair.classList.toggle('is-gone', !p);
        if (p) { chair.style.left = p[0] - 8 + 'px'; chair.style.top = p[1] - 8 + 'px'; }
      });
    }
  }

  if (!customElements.get('teak-cfg')) customElements.define('teak-cfg', TeakCfg);

  function initRoot(root) {
    if (root.__teakShowroom) return;
    root.__teakShowroom = true;
    const currency = root.dataset.currency || 'AUD';

    // Photo dots follow the swipe.
    const track = root.querySelector('[data-teak-pshow-track]');
    const dots = root.querySelectorAll('[data-teak-pshow-dot]');
    if (track && dots.length) {
      let ticking = false;
      const sync = () => {
        ticking = false;
        const i = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
        dots.forEach((dot, k) => dot.classList.toggle('is-active', k === i));
      };
      track.addEventListener('scroll', () => { if (!ticking) { ticking = true; requestAnimationFrame(sync); } }, { passive: true });
    }

    // A story photo opens the full-screen viewer on that same photo.
    root.querySelectorAll('[data-teak-story-open]').forEach(btn => {
      btn.addEventListener('click', () => {
        const trigger = root.querySelector('[data-teak-pshow-lightbox-trigger][data-index="' + btn.getAttribute('data-teak-story-open') + '"]');
        if (trigger) trigger.click();
      });
    });

    // "Add to cart · $2,090", times the quantity.
    const qty = root.querySelector('[data-teak-pdetails-qty]');
    const label = root.querySelector('[data-teak-pdetails-form] .teak-pshow__atc .add-to-cart-text__content');
    let unit = parseInt(root.dataset.price, 10) || 0;
    const paint = () => {
      if (!label || !unit) return;
      const n = Math.max(1, parseInt(qty && qty.value, 10) || 1);
      label.setAttribute('data-price', money(unit * n, currency));
    };
    root.addEventListener('teak:variant-applied', e => { unit = e.detail.variant.price; paint(); });
    if (qty) { qty.addEventListener('input', paint); qty.addEventListener('change', paint); }
    root.addEventListener('click', e => {
      if (e.target.closest('[data-teak-pdetails-qty-up], [data-teak-pdetails-qty-down]')) paint();
    });
    paint();
  }

  // Phones: the measurements open first, as in design B. The story above the
  // tabs already carries the opening of the description, and its "Read more"
  // switches to the Description tab.
  function initTabs(tabs) {
    const pills = tabs.querySelector('.teak-ptabs__pills');
    if (pills) {
      const fit = () => {
        pills.classList.remove('is-wrapped');
        if (pills.scrollWidth > pills.clientWidth + 1) pills.classList.add('is-wrapped');
      };
      fit();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(fit);
      window.addEventListener('resize', fit, { passive: true });
    }
    if (!window.matchMedia('(max-width: 749px)').matches) return;
    const pill = tabs.querySelector('.teak-ptabs__pill[data-teak-ptabs-tab="dimensions"]');
    if (pill && !pill.classList.contains('is-active')) pill.click();
  }

  const start = () => {
    document.querySelectorAll('.teak-pshow--showroom[data-teak-pshow]').forEach(initRoot);
    document.querySelectorAll('.teak-ptabs--showroom').forEach(initTabs);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
